import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import { loadTurnstileScript, mapI18nToTurnstileLang } from './utils'

/**
 * TurnstileWidget — wrapper React del widget Cloudflare Turnstile.
 *
 * Explicit rendering (SPA-friendly, controllo lifecycle).
 * Script caricato async una sola volta per pagina (loadTurnstileScript dedupla).
 *
 * Props:
 *   siteKey       string  (default: process.env.GATSBY_TURNSTILE_SITE_KEY)
 *   theme         'auto' | 'light' | 'dark'  (default 'auto')
 *   size          'normal' | 'flexible' | 'compact' (default 'flexible')
 *   onSuccess     (token: string) => void
 *   onError       (errorCode: string) => void
 *   onExpired     () => void
 *   className     string (classe wrapper opzionale)
 *
 * Ref API (forwardRef):
 *   reset()   — resetta il widget (utile dopo submit fallito)
 *   getToken() — ritorna il token corrente (o null)
 *
 * Accessibilità:
 *   contenitore ha role="group" e aria-label localizzato.
 *   Stato "loading" e "error" annunciati via aria-live nel form parent.
 */
const TurnstileWidget = forwardRef(function TurnstileWidget(
  {
    siteKey = process.env.GATSBY_TURNSTILE_SITE_KEY,
    theme = 'auto',
    size = 'flexible',
    onSuccess,
    onError,
    onExpired,
    className,
  },
  ref
) {
  const { i18n, t } = useTranslation()
  const containerRef = useRef(null)
  const widgetIdRef = useRef(null)
  const [loadError, setLoadError] = useState(null)

  useImperativeHandle(ref, () => ({
    reset() {
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.reset(widgetIdRef.current)
      }
    },
    getToken() {
      if (window.turnstile && widgetIdRef.current) {
        return window.turnstile.getResponse(widgetIdRef.current) || null
      }
      return null
    },
  }))

  useEffect(() => {
    let cancelled = false

    if (!siteKey) {
      setLoadError('turnstile_no_sitekey')
      return
    }

    loadTurnstileScript()
      .then(turnstile => {
        if (cancelled || !containerRef.current) return
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          size,
          language: mapI18nToTurnstileLang(i18n.language),
          callback: token => onSuccess && onSuccess(token),
          'error-callback': code => onError && onError(code),
          'expired-callback': () => onExpired && onExpired(),
        })
      })
      .catch(err => {
        if (cancelled) return
        setLoadError(err.message || 'turnstile_load_error')
        if (onError) onError(err.message || 'turnstile_load_error')
      })

    return () => {
      cancelled = true
      if (window.turnstile && widgetIdRef.current) {
        try { window.turnstile.remove(widgetIdRef.current) } catch (e) { /* ignore */ }
        widgetIdRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey, theme, size, i18n.language])

  if (loadError) {
    return (
      <div
        className={className}
        role="alert"
        aria-live="polite"
      >
        {t('Anti-bot check could not be loaded.')}
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={className}
      role="group"
      aria-label={t('Anti-bot verification')}
    />
  )
})

export default TurnstileWidget
